import { Directive, Input, OnInit, OnDestroy, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ViewTrackingService, ViewType } from '../services/view-tracking.service';

/**
 * Directive for tracking views of profiles and job offers
 * 
 * Features:
 * - Tracks viewing time using Page Visibility API
 * - Registers view only after 10 seconds of continuous viewing
 * - Prevents duplicates using session tokens
 * - Automatically pauses when page is not visible
 * 
 * Usage:
 * <div appViewTracker [viewType]="ViewType.IndependentProfile" [entityId]="resumeId">
 */
@Directive({
  selector: '[appViewTracker]',
  standalone: true
})
export class ViewTrackerDirective implements OnInit, OnDestroy {
  @Input() viewType!: ViewType; // View type: 1=Profile, 2=Job Offer
  @Input() entityId!: number;   // ID of the entity to track
  
  private visibilityTimer?: number;
  private accumulatedTime = 0;
  private isVisible = true;
  private viewRegistered = false;
  private sessionToken: string;
  private readonly REQUIRED_TIME = 10000; // Minimum required time: 10 seconds
  private startTime?: number;
  
  constructor(
    private viewTrackingService: ViewTrackingService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.sessionToken = this.viewTrackingService.generateSessionToken();
  }
  
  ngOnInit() {
    // Validate required parameters before initializing tracking
    if (!this.viewType || !this.entityId) {
      console.warn('ViewTracker: viewType and entityId are required');
      return;
    }
    
    this.setupVisibilityTracking();
    this.startTracking();
  }
  
  ngOnDestroy() {
    this.cleanup();
  }
  
  private setupVisibilityTracking() {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    // Page Visibility API
    this.document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    
    // Window focus/blur events as fallback
    window.addEventListener('focus', this.handleWindowFocus.bind(this));
    window.addEventListener('blur', this.handleWindowBlur.bind(this));
    
    // Before unload to handle page navigation
    window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this));
  }
  
  private handleVisibilityChange() {
    if (this.document.visibilityState === 'visible') {
      this.handleWindowFocus();
    } else {
      this.handleWindowBlur();
    }
  }
  
  private handleWindowFocus() {
    if (!this.viewRegistered && !this.isVisible) {
      this.isVisible = true;
      this.startTracking();
    }
  }
  
  private handleWindowBlur() {
    if (this.isVisible) {
      this.isVisible = false;
      this.pauseTracking();
    }
  }
  
  private handleBeforeUnload() {
    this.pauseTracking();
  }
  
  private startTracking() {
    if (this.viewRegistered || !this.isVisible) return;
    
    const timeLeft = this.REQUIRED_TIME - this.accumulatedTime;
    this.startTime = Date.now();
    
    // Start timer to complete the required viewing time
    this.visibilityTimer = setTimeout(() => {
      this.registerView();
    }, timeLeft) as any;
  }
  
  private pauseTracking() {
    if (this.visibilityTimer) {
      // Pause timer and accumulate elapsed time
      if (this.startTime) {
        this.accumulatedTime += Date.now() - this.startTime;
      }
      clearTimeout(this.visibilityTimer);
      this.visibilityTimer = undefined;
    }
    
    if (this.startTime && this.isVisible) {
      this.accumulatedTime += Date.now() - this.startTime;
      this.startTime = undefined;
    }
  }
  
  private registerView() {
    if (this.viewRegistered) return;
    
    this.viewRegistered = true;
    
    // Register view in the backend
    const request = {
      viewType: this.viewType,
      entityId: this.entityId,
      sessionToken: this.sessionToken
    };
    
    this.viewTrackingService.registerView(request).subscribe({
      next: (response: any) => {
        // View registered successfully
      },
      error: (error: any) => {
        console.error('Error registering view:', error);
        // Reset to allow retry in next session
        this.viewRegistered = false;
      }
    });
  }
  
  private cleanup() {
    if (this.visibilityTimer) {
      clearTimeout(this.visibilityTimer);
    }
    
    // Check if we're in a browser environment before removing listeners
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      this.document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
      window.removeEventListener('focus', this.handleWindowFocus.bind(this));
      window.removeEventListener('blur', this.handleWindowBlur.bind(this));
      window.removeEventListener('beforeunload', this.handleBeforeUnload.bind(this));
    }
  }
}